import {
  API_PATHS,
  getAxiosResponseData,
  interpolatePathParams,
  REQUEST_METHODS,
  ROUTE_PARAMS
} from '@futrli/shared-services';
import { WorkspaceMember } from '@futrli-sdk/people';

import { APIClients } from '@futrli/shared-services';
import { WorkspaceMemberPatch, WorkspaceMemberRolePatch } from '../types';

/**
 * Fetches and returns a full list of members of the workspace
 *
 * @function
 * @name fetchMembers
 * @param {string} workspaceId - Workspace id.
 * @returns {Promise<WorkspaceMember[]>} - A full list of members of the workspace
 */
export const fetchMembers = async (workspaceId: string): Promise<WorkspaceMember[]> => {
  const path = interpolatePathParams(API_PATHS.MEMBERS, { [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });

  return getAxiosResponseData(response).data;
};

/**
 * Fetches and returns a full list of members of the organisation within workspace
 *
 * @function
 * @name fetchMembersInOrganisation
 * @param {string} workspaceId - Workspace id.
 * @returns {Promise<WorkspaceMember[]>} - A full list of members of the workspace
 */
export const fetchMembersInOrganisation = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<WorkspaceMember[]> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.MEMBERS_BY_ORGANISATION_CONSOLIDATION : API_PATHS.MEMBERS_BY_ORGANISATION,
    {
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
    }
  );
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });

  return getAxiosResponseData(response).data;
};

/**
 * Triggers a delete for a member on the workspace
 *
 * @function
 * @name deleteMember
 * @param {string} workspaceId - Workspace id.
 * @param {string} memberId - Member id.
 * @returns {Promise<WorkspaceMember[]>} - The response
 */
export const deleteMember = async (workspaceId: string, memberId: string): Promise<WorkspaceMember[]> => {
  const path = interpolatePathParams(API_PATHS.MEMBER_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.MEMBER_ID]: memberId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.DELETE,
        url: path
      }
    }
  });

  return response.data;
};

/**
 * Updates a workspace member's role.
 *
 * Organisation access is managed by addOrganisationToMember and deleteOrganisationFromMember.
 *
 * @param {WorkspaceMemberRolePatch} payload - The role payload
 * @param {string} workspaceId - Workspace id.
 * @param {string} memberId - Member id.
 * @returns {Promise<WorkspaceMember>} - The person with the new role attached
 */
export const updateMember = async (
  payload: WorkspaceMemberRolePatch,
  workspaceId: string,
  memberId: string
): Promise<WorkspaceMember> => {
  const path = interpolatePathParams(API_PATHS.MEMBER_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.MEMBER_ID]: memberId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PATCH,
        url: path,
        data: payload
      }
    }
  });

  return getAxiosResponseData(response).data;
};

/**
 * Triggers a delete for a member on the workspace
 *
 * @function
 * @name deleteOrganisationFromMember
 * @param {string} workspaceId - Workspace id.
 * @param {string} memberId - Member id.
 * @param {string} organisationId - Organisation id.
 * @returns {Promise<WorkspaceMember>} - The response
 */
export const deleteOrganisationFromMember = async (
  workspaceId: string,
  memberId: string,
  organisationId: string
): Promise<WorkspaceMember> => {
  const path = interpolatePathParams(API_PATHS.DELETE_MEMBER_ORGANISATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.MEMBER_ID]: memberId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.DELETE,
        url: path
      }
    }
  });

  return response.data;
};

/**
 * Triggers a put with orgs for a member on the workspace
 *
 * @function
 * @name addOrganisationToMember
 * @param {Partial<WorkspaceMemberRole>} payload - The role payload
 * @param {string} workspaceId - Workspace id.
 * @param {string} memberId - Member id.
 * @returns {Promise<WorkspaceMember>} - The response
 */
export const addOrganisationToMember = async (
  payload: WorkspaceMemberPatch,
  workspaceId: string,
  memberId: string
): Promise<WorkspaceMember> => {
  const path = interpolatePathParams(API_PATHS.ADD_MEMBER_ORGANISATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.MEMBER_ID]: memberId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: path,
        data: payload
      }
    }
  });

  return getAxiosResponseData(response).data;
};

/**
 * Sends the list of users to add into the workspace, returns new members.
 *
 * @function
 * @name createInvitation
 * @param {string} workspaceId - Workspace id.
 * @param {WorkspaceMemberPatch[]} invitation - Invitation payload.
 * @throws {Error} In case of having issues creating the invitation.
 * @returns {Promise<WorkspaceMember[]>}
 */
export const createInvitation = async (
  workspaceId: string,
  invitation: WorkspaceMemberPatch[]
): Promise<WorkspaceMember[]> => {
  const path = interpolatePathParams(API_PATHS.MEMBERS, { [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId });

  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: path,
        data: invitation
      }
    }
  });

  return getAxiosResponseData(response).data;
};

/**
 * Sends a request to resend email invitation
 *
 * @function
 * @name resendInvite
 * @param {string} workspaceId - Workspace id.
 * @param {string} memberId - Member id.
 * @returns {Promise<void>} - New role.
 */
export const resendInvite = async (workspaceId: string, memberId: string): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.RESEND_INVITE, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.MEMBER_ID]: memberId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: path
      }
    }
  });

  return getAxiosResponseData(response.data);
};
